import { useState, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Button } from '@simplywallst/ui-core';
import LikeOutline from '@simplywallst/ui-core/icons/LikeOutline';
import LikeSolid from '@simplywallst/ui-core/icons/LikeSolid';

import If from '@/components/If';
import useUser from '@/hooks/useUser';

import { useVoteOnNarrative } from '../../hooks/useVoteOnNarrative';
import { useNarrativePermission } from '../../hooks/useNarrativePermission';

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal')
);

export interface Props {
  narrativeId: string;
  slug: string;
  upVotes: number;
}

const NarrativeVoting = ({ narrativeId, slug, upVotes }: Props) => {
  const [showRegoModal, setShowRegoModal] = useState(false);
  const { userVotedNarratives } = useNarrativePermission();
  const isUpVoted = useMemo(
    () => !!userVotedNarratives?.find((n) => n.narrativeId === narrativeId),
    [userVotedNarratives, narrativeId]
  );
  const { mutateAsync: toggleVote } = useVoteOnNarrative(slug, isUpVoted);
  const {
    data: { loggedIn },
  } = useUser();
  const { trackEvent } = useTracking();

  return (
    <>
      <If match={showRegoModal}>
        {() => (
          <GuestRegistrationModal
            canClose={true}
            removeWindowScroll={true}
            onLoginSuccess={() => {
              setShowRegoModal(false);
            }}
            onRegisterSuccess={() => {
              setShowRegoModal(false);
            }}
            onClose={() => {
              setShowRegoModal(false);
              trackEvent({
                type: 'track',
                subject: 'guest registration modal',
                modifier: 'dismissed',
                action: 'click',
              });
            }}
          />
        )}
      </If>

      <Button
        data-cy-id="narrative-upvote"
        styleType="gentle"
        alignIcon="left"
        onClick={() => {
          if (!loggedIn) {
            setShowRegoModal(true);
            return;
          }

          toggleVote({ narrativeId });
          if (isUpVoted) {
            trackEvent({ action: 'clicked', modifier: 'downVote' });
          } else {
            trackEvent({ action: 'clicked', modifier: 'upVote' });
          }
        }}
      >
        {isUpVoted ? <LikeSolid /> : <LikeOutline />}
        {upVotes}
        <span className="sr-only"> votes</span>
      </Button>
    </>
  );
};

export default NarrativeVoting;
